import React, { useContext, useEffect, useRef, useState } from "react";
import { useTeacherNotifContext } from "../../../components/gcstaff/teachertopbar/TeacherTopbar";
import "../../../assets/css/gcstaff/teachernotifications/TeacherNotificationsPage.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

/* ─── Notification Icon ─── */
const NotifIcon = ({ type, size = "md" }) => {
  const colorMap = {
    overdue: "gcTeacherNP-icon--danger",
    book:    "gcTeacherNP-icon--success",
    review:  "gcTeacherNP-icon--info",
    dispute: "gcTeacherNP-icon--warning",
  };
  const symbols = { overdue: "!", book: "✦", review: "★", dispute: "⚠" };
  return (
    <span className={`gcTeacherNP-icon gcTeacherNP-icon--${size} ${colorMap[type] || colorMap.review}`} aria-hidden="true">
      {symbols[type] || "●"}
    </span>
  );
};

/* ─── Main TeacherNotificationsPage ─── */
const ITEMS_PER_PAGE = 10; // yahan se page size control hoti hai

const TeacherNotificationsPage = () => {
  const { baseUrl, loggedInUser, authToken } = useContext(AppContext);

  const ctx = useTeacherNotifContext();
  const [localNotifications, setLocalNotifications] = useState([]);

  const notifications    = ctx ? ctx.notifications    : localNotifications;
  const setNotifications = ctx ? ctx.setNotifications : setLocalNotifications;

  const [loading, setLoading]         = useState(false); // initial API fetch loading
  const [viewLoading, setViewLoading] = useState(false); // page switch loading (sirf visible notifications k liye)
  const [currentPage, setCurrentPage] = useState(1);      // pagination state

  // Pehli render (initial mount) par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  const unreadCount = notifications?.filter((n) => n.status === "unread").length;

  /* ── Pagination slice (sirf current page ki notifications list mein jayengi) ── */
  const paginatedNotifications = notifications.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const selectNotif = (n) => {
    setNotifications((prev) =>
      prev.map((item) => (item.id === n.id ? { ...item, status: "read" } : item))
    );
  };

  /* ── Fetch all notifications ── */
  const fetchAllNotifications = () => {
    if (!loggedInUser?.id) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/notifications/fetchAllNotifications`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setLocalNotifications(res.data.notifications);
        } else {
          setLocalNotifications([]);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* ── Mark all as read ── */
  const markAllAsRead = () => {
    if (!loggedInUser?.id) return;

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/notifications/markAllAsRead`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          Swal.fire({
            title: 'Wow',
            icon: 'success',
            text: 'All Notifcations Marked As Read',
            confirmButtonText: 'Okay'
          });
          fetchAllNotifications();
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  /* ── Clear all notifications ── */
  const clearAllNotifications = () => {
    if (!loggedInUser?.id) return;

    Swal.fire({
      title: 'Clear All Notifications?',
      text: 'This will permanently delete all your notifications.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, clear all',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("lbteacher_id", loggedInUser.id);

        axios
          .post(`${baseUrl}/teacher/notifications/deleteAllNotifications`, formData, {
            headers: {
              "Content-Type": "multipart/formData",
              "Authorization": `Bearer ${authToken}`,
            },
          })
          .then((res) => {
            if (res.data.success === true) {
              setLocalNotifications([]);
              setCurrentPage(1);
            }
          })
          .catch((err) => console.log(err));
      }
    });
  };

  useEffect(() => {
    if (loggedInUser?.id) {
      fetchAllNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  /* ── Sirf visible notifications ki loading dikhayein — jab pagination
     se dusre page par jayen. Poori list ek sath "pop" nahi hogi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage]);

  return (
    <div className="gcTeacherNP-root">

      {/* ── Page Header ── */}
      <div className="gcTeacherNP-page-head">
        <div>
          <h1 className="gcTeacherNP-page-title">Notifications</h1>
          <p className="gcTeacherNP-page-sub">Stay updated with your students' activity and alerts.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {notifications.length > 0 && (
            <button
              className="gcTeacherNP-markall-btn"
              onClick={markAllAsRead}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 7L5.5 10.5L12 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Mark All as Read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              className="gcTeacherNP-markall-btn"
              style={{ color: "#d33", borderColor: "#d33" }}
              onClick={clearAllNotifications}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 3.5H12M5 3.5V2.5H9V3.5M4 3.5V11.5H10V3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Notifications loading, Please wait..." />}

      {/* ── Layout ── */}
      {!loading && (
        <div className="gcTeacherNP-layout">

          {/* ── List ── */}
          <div className="gcTeacherNP-list-col">
            <div className="gcTeacherNP-section-head">
              <span className="gcTeacherNP-section-head__dot" aria-hidden="true" />
              <h2 className="gcTeacherNP-section-head__title">Recent Notifications</h2>
              {unreadCount > 0 && (
                <span className="gcTeacherNP-section-head__badge">{unreadCount}</span>
              )}
            </div>

            {viewLoading ? (
              /* ── Page switch loading — sirf is section ki, baaki page affect nahi hota ── */
              <Loader message="Loading notifications..." />
            ) : (
              <ul className="gcTeacherNP-list" role="list">
                {paginatedNotifications.map((n, idx) => (
                  <li
                    key={n.id}
                    className={[
                      "gcTeacherNP-row",
                      n.status === "unread" ? "gcTeacherNP-row--unread" : "",
                    ].filter(Boolean).join(" ")}
                    style={{ animationDelay: `${idx * 0.055}s` }}
                    onClick={() => selectNotif(n)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && selectNotif(n)}
                  >
                    <NotifIcon type={n.type} />
                    <div className="gcTeacherNP-row__body">
                      <div className="gcTeacherNP-row__top">
                        <span className="gcTeacherNP-row__title">
                          {n.title}
                          {n.status === "unread" && <span className="gcTeacherNP-row__dot" aria-label="unread" />}
                        </span>
                      </div>
                      <p className="gcTeacherNP-row__summary">{n.subtitle}</p>
                    </div>
                    {n.status === "read" && (
                      <span className="gcTeacherNP-row__read-tick" aria-label="Read">
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                          <path d="M2 6.5L5 9.5L11 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    )}
                  </li>
                ))}

                {notifications.length === 0 && (
                  <li className="gcTeacherNP-empty">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <path d="M20 5a11 11 0 0111 11v6l3 4H6l3-4v-6A11 11 0 0120 5zM17 35a3 3 0 006 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                    <span>No notifications yet</span>
                  </li>
                )}
              </ul>
            )}

            {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
            <Pagination
              currentPage={currentPage}
              totalItems={notifications.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          </div>

        </div>
      )}
    </div>
  );
};

export default TeacherNotificationsPage;